import React, { useEffect, useMemo, useState } from "react";
import { db, auth } from "../firebase";
import { sendPasswordResetEmail } from "firebase/auth";
import { collection, deleteDoc, doc, onSnapshot, setDoc } from "firebase/firestore";
import { motion, AnimatePresence } from "framer-motion";

const OWNER_EMAIL = "benjamin@mcins.ca";
const toEmailLower = (email) => String(email || "").trim().toLowerCase();

function isInfiniteValue(v) {
  if (v === "-" || v === "∞") return true;
  if (typeof v === "number" && !Number.isFinite(v)) return true;
  return false;
}

function normalizeStoredNumberOrDash(v, fallback = 0) {
  // Keep "-" as "-" (infinite).
  if (typeof v === "string") {
    const t = v.trim();
    if (t === "-") return "-";
    if (t === "∞") return "-";
    if (t === "") return fallback;
    const n = Number(t);
    return Number.isFinite(n) ? n : fallback;
  }
  if (typeof v === "number") return Number.isFinite(v) ? v : "-";
  return fallback;
}

function displayDays(v) {
  if (isInfiniteValue(v)) return "∞";
  const n = Number(v);
  return Number.isFinite(n) ? n : "—";
}

function displayRole(role) {
  const r = String(role || "employee").toLowerCase();
  return r === "admin" ? "Admin" : "Employee";
}

export default function AdminConsole() {
  const initialFormState = {
    name: "",
    email: "",
    role: "employee",
    annualAllotmentDays: 15,
    flexAllotmentDays: 3, 
    sickAllotmentDays: 10, // Default for Sick days
    workFromHomeAllotmentDays: 0,
  };

  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(initialFormState);
  const [editingDocId, setEditingDocId] = useState(null);
  const [showUserForm, setShowUserForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const ownerLower = toEmailLower(OWNER_EMAIL);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "users"),
      (snap) => {
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        const cleaned = data
          .map((u) => {
            const email = u.email || "";
            const emailLower = u.emailLower || toEmailLower(email);
            const name = u.name || u.displayName || (email ? email.split("@")[0] : u.id);
            const role = (u.role || "employee").toLowerCase();

            return {
              ...u,
              email,
              emailLower,
              name,
              role,
              annualAllotmentDays: normalizeStoredNumberOrDash(u.annualAllotmentDays, 15),
              flexAllotmentDays: normalizeStoredNumberOrDash(u.flexAllotmentDays, 3), 
              sickAllotmentDays: normalizeStoredNumberOrDash(u.sickAllotmentDays, 10), // Load sick days
              workFromHomeAllotmentDays: normalizeStoredNumberOrDash(u.workFromHomeAllotmentDays, 0),
            };
          })
          .sort((a, b) => String(a.name).localeCompare(String(b.name)));

        setUsers(cleaned);
        setLoading(false);
      },
      (e) => {
        console.error("AdminConsole users listener error:", e);
        setError("Failed to load users (permissions?).");
        setLoading(false);
      }
    );

    return () => unsub();
  }, []);

  const isEditingOwner = useMemo(() => {
    if (!editingDocId) return false;
    const u = users.find((x) => x.id === editingDocId);
    const emailLower = toEmailLower(u?.emailLower || u?.email || "");
    return emailLower === ownerLower;
  }, [editingDocId, users, ownerLower]);

  function startAddUser() {
    setEditingDocId(null);
    setForm(initialFormState);
    setShowUserForm((prev) => !prev);
    setError("");
    setNotice("");
  }

  function startEdit(u) {
    setEditingDocId(u.id);
    setForm({
      name: u.name || "",
      email: u.email || "",
      role: (u.role || "employee").toLowerCase() === "admin" ? "admin" : "employee",
      annualAllotmentDays: normalizeStoredNumberOrDash(u.annualAllotmentDays, 15),
      flexAllotmentDays: normalizeStoredNumberOrDash(u.flexAllotmentDays, 3),
      sickAllotmentDays: normalizeStoredNumberOrDash(u.sickAllotmentDays, 10),
      workFromHomeAllotmentDays: normalizeStoredNumberOrDash(u.workFromHomeAllotmentDays, 0),
    });
    setShowUserForm(true);
    setError("");
    setNotice("");
  }

  async function saveUser(e) {
    e.preventDefault();
    setError("");
    setNotice("");

    const emailLower = toEmailLower(form.email);
    if (!emailLower.includes("@")) {
      setError("Please enter a valid email.");
      return;
    }

    const isOwner = emailLower === ownerLower;

    try {
      // Add new user: store doc under emailLower (stable)
      // Edit existing: keep doc id
      const docId = editingDocId || emailLower;

      const vacationDays = normalizeStoredNumberOrDash(form.annualAllotmentDays, 15);
      const flexDays = normalizeStoredNumberOrDash(form.flexAllotmentDays, 3);
      const sickDays = normalizeStoredNumberOrDash(form.sickAllotmentDays, 10);
      const wfhDays = normalizeStoredNumberOrDash(form.workFromHomeAllotmentDays, 0);

      const payload = {
        name: form.name,
        email: form.email,
        emailLower,
        role: isOwner ? "admin" : form.role, // Benjamin always admin
        annualAllotmentDays: vacationDays,
        flexAllotmentDays: flexDays,
        sickAllotmentDays: sickDays,
        workFromHomeAllotmentDays: wfhDays,
        updatedAt: new Date().toISOString(),
      };

      await setDoc(doc(db, "users", docId), payload, { merge: true });

      setForm(initialFormState);
      setEditingDocId(null);
      setShowUserForm(false);
      setNotice("User saved.");
    } catch (err) {
      console.error("Error saving user:", err);
      setError("Failed to save user (permissions?).");
    }
  }

  async function removeUser(u) {
    setError("");
    setNotice("");
    const emailLower = toEmailLower(u.emailLower || u.email || "");
    if (emailLower === ownerLower) {
      setError("Benjamin cannot be deleted.");
      return;
    }
    if (!window.confirm(`Delete ${u.name || u.email || u.id}?`)) return;

    try {
      await deleteDoc(doc(db, "users", u.id));
      setNotice("User deleted.");
    } catch (err) {
      console.error("Error deleting user:", err);
      setError("Failed to delete user (permissions?).");
    }
  }

  async function resetPassword(email) {
    setError("");
    setNotice("");

    const clean = String(email || "").trim();
    if (!clean || !clean.includes("@")) {
      setError("No valid email found for this user.");
      return;
    }

    if (!window.confirm(`Send password reset email to ${clean}?`)) return;

    try {
      await sendPasswordResetEmail(auth, clean);
      setNotice(`Password reset email sent to ${clean}.`);
    } catch (err) {
      console.error("Reset password error:", err);

      // friendlier messages for common Firebase Auth errors
      const code = err?.code || "";
      if (code.includes("auth/user-not-found")) {
        setError("That email is not an Email/Password account in Firebase Auth (or doesn't exist).");
      } else if (code.includes("auth/invalid-email")) {
        setError("That email address is invalid.");
      } else if (code.includes("auth/operation-not-allowed")) {
        setError("Email/Password sign-in is not enabled in Firebase Auth.");
      } else {
        setError("Could not send reset email. Check Auth provider + console logs.");
      }
    }
  }

  return (
    <div className="p-6 max-w-7xl mx-auto text-gray-800 dark:text-gray-100">
      <div className="flex items-start justify-between gap-4">
        <h1 className="text-2xl font-bold mb-2">Admin</h1>
        <button
          onClick={startAddUser}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm"
        >
          {showUserForm ? (editingDocId ? "Close Edit User" : "Hide User Form") : "Add New User"}
        </button>
      </div>

      {error && (
        <div className="bg-red-100 text-red-700 border border-red-300 rounded p-3 mb-4">
          {error}
        </div>
      )}
      {notice && (
        <div className="bg-green-100 text-green-800 border border-green-300 rounded p-3 mb-4">
          {notice}
        </div>
      )}

      <div className="border rounded-xl p-4 bg-white dark:bg-gray-800 shadow-sm mb-4">
        <h2 className="font-semibold mb-4 text-lg">Users</h2>

        {loading ? (
          <p>Loading users...</p>
        ) : users.length === 0 ? (
          <p className="text-gray-500 text-sm">No users found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 dark:bg-gray-700/40">
                  <th className="p-2 text-left">Name</th>
                  <th className="p-2 text-left">Email</th>
                  <th className="p-2 text-left">Role</th>
                  <th className="p-2 text-center">Vacation</th>
                  <th className="p-2 text-center">Flex</th>
                  <th className="p-2 text-center">Sick</th>
                  <th className="p-2 text-center">WFH</th>
                  <th className="p-2 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {users.map((u) => {
                    const emailLower = toEmailLower(u.emailLower || u.email || "");
                    const locked = emailLower === ownerLower;

                    return (
                      <motion.tr
                        key={u.id}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.15 }}
                        className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/20"
                      >
                        <td className="p-2">
                          <div className="font-medium">{u.name || "—"}</div>
                          {locked && (
                            <div className="text-xs text-blue-700 dark:text-blue-300">
                              Locked (Owner)
                            </div>
                          )}
                        </td>
                        <td className="p-2">{u.email || "—"}</td>
                        <td className="p-2">
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs ${
                              (u.role || "").toLowerCase() === "admin"
                                ? "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200"
                                : "bg-gray-100 text-gray-700 dark:bg-gray-700/40 dark:text-gray-200"
                            }`}
                          >
                            {displayRole(u.role)}
                          </span>
                        </td>
                        <td className="p-2 text-center font-semibold">
                          {displayDays(u.annualAllotmentDays)}
                        </td>
                        <td className="p-2 text-center font-semibold text-purple-700 dark:text-purple-300">
                          {displayDays(u.flexAllotmentDays)}
                        </td>
                        <td className="p-2 text-center font-semibold text-stone-700 dark:text-stone-300">
                          {displayDays(u.sickAllotmentDays)}
                        </td>
                        <td className="p-2 text-center font-semibold">
                          {displayDays(u.workFromHomeAllotmentDays)}
                        </td>
                        <td className="p-2 text-center space-x-3">
                          <button
                            onClick={() => resetPassword(u.email)}
                            className="hover:underline text-indigo-600"
                            title="Sends a reset email (Email/Password accounts only)"
                          >
                            Reset
                          </button>

                          <button
                            onClick={() => startEdit(u)}
                            className={`hover:underline ${
                              locked ? "text-gray-400 cursor-not-allowed" : "text-blue-600"
                            }`}
                            disabled={locked}
                          >
                            Edit
                          </button>

                          <button
                            onClick={() => removeUser(u)}
                            className={`hover:underline ${
                              locked ? "text-gray-400 cursor-not-allowed" : "text-red-600"
                            }`}
                            disabled={locked}
                          >
                            Delete
                          </button>
                        </td>
                      </motion.tr>
                    );
                  })}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showUserForm && (
        <div className="border rounded-xl p-4 bg-white dark:bg-gray-800 shadow-sm mb-6">
          <h2 className="font-semibold mb-3 text-lg">
            {editingDocId ? "Edit User" : "Add New User"}
          </h2>

          {isEditingOwner && (
            <div className="mb-3 text-sm rounded border border-blue-200 bg-blue-50 text-blue-800 p-3">
              Benjamin is locked. You can’t edit or delete this account.
            </div>
          )}

          <form onSubmit={saveUser} className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="flex flex-col">
              <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
                Name
              </label>
              <input
                type="text"
                required
                className="border p-2 rounded dark:bg-gray-700"
                value={form.name}
                disabled={isEditingOwner}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>

            <div className="flex flex-col">
              <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
                Email
              </label>
              <input
                type="email"
                required
                className="border p-2 rounded dark:bg-gray-700"
                value={form.email}
                disabled={isEditingOwner}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>

            <div className="flex flex-col">
              <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
                Role
              </label>
              <select
                className="border p-2 rounded dark:bg-gray-700"
                value={form.role}
                disabled={isEditingOwner}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
              >
                <option value="employee">Employee</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            <div className="flex flex-col">
              <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
                Vacation Days
              </label>
              <input
                type="text"
                className="border p-2 rounded dark:bg-gray-700"
                value={String(form.annualAllotmentDays)}
                disabled={isEditingOwner}
                onChange={(e) => setForm({ ...form, annualAllotmentDays: e.target.value })}
                placeholder='e.g. 15 or "-"'
              />
            </div>

            <div className="flex flex-col">
              <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
                Flex Days
              </label>
              <input
                type="text"
                className="border p-2 rounded dark:bg-gray-700"
                value={String(form.flexAllotmentDays)}
                disabled={isEditingOwner}
                onChange={(e) => setForm({ ...form, flexAllotmentDays: e.target.value })}
                placeholder='e.g. 3'
              />
            </div>

             <div className="flex flex-col">
              <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
                Sick Days
              </label>
              <input
                type="text"
                className="border p-2 rounded dark:bg-gray-700"
                value={String(form.sickAllotmentDays)}
                disabled={isEditingOwner}
                onChange={(e) => setForm({ ...form, sickAllotmentDays: e.target.value })}
                placeholder='e.g. 10'
              />
            </div>

            <div className="flex flex-col">
              <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
                WFH Days
              </label>
              <input
                type="text"
                className="border p-2 rounded dark:bg-gray-700"
                value={String(form.workFromHomeAllotmentDays)}
                disabled={isEditingOwner}
                onChange={(e) =>
                  setForm({ ...form, workFromHomeAllotmentDays: e.target.value })
                }
                placeholder='e.g. 0 or "-"'
              />
            </div>
            
            <div className="md:col-span-4 text-[11px] text-gray-500">
               Use <span className="font-semibold">-</span> for unlimited.
            </div>

            <button
              type="submit"
              disabled={isEditingOwner}
              className={`px-4 py-2 rounded md:col-span-4 ${
                isEditingOwner
                  ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                  : "bg-blue-600 text-white hover:bg-blue-700"
              }`}
            >
              {editingDocId ? "Update User" : "Add User"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}